import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { BatteryArbitrageDto } from "../dto/battery-arbitrage.dto"

/**
 * The Proposal read carries its own copy of the current arbitrage result for
 * the presentation, and the gateway withholds that copy once the saved run no
 * longer matches current inputs. Re-reading it whenever the lifecycle moves is
 * what stops the page behind this drawer from going on showing savings the
 * drawer already calls outdated — the two money claims move together.
 */
export const invalidateBatteryProposalForArbitrage = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.savingsReport(proposalId),
  })
  void queryClient.invalidateQueries({
    exact: true,
    queryKey: queryKeys.batteryProposal.byId(proposalId),
  })
}

/**
 * Publish an arbitrage write's own response, then re-read the Proposal.
 *
 * Both endpoints answer with the same contract the GET returns, so the response
 * is the new state — writing it means the seller never sees the superseded
 * savings again, not even for one frame.
 *
 * Cancelling first is what makes that true. While a run is in flight this query
 * polls, so a GET issued just before the write resolves just after it, and
 * React Query has no revision guard: that older body would land on top of the
 * write's answer and present a result computed from the inputs the seller just
 * replaced. Same reasoning as `useCompleteBatteryInitialSetup`.
 */
export const publishBatteryArbitrage = async (
  queryClient: QueryClient,
  proposalId: string,
  arbitrage: BatteryArbitrageDto,
): Promise<void> => {
  await queryClient.cancelQueries({
    queryKey: queryKeys.batteryProposal.arbitrage(proposalId),
  })
  queryClient.setQueryData(
    queryKeys.batteryProposal.arbitrage(proposalId),
    arbitrage,
  )
  invalidateBatteryProposalForArbitrage(queryClient, proposalId)
}
